import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AlertCircle, Check, ChevronsUpDown, Layers, Plus } from 'lucide-react';
import type { Mailbox } from '@mailpilot/shared';
import { Menu } from '../ui';
import { useAccountFilter } from '../../lib/accountFilter';
import { useInboxUnreadByMailbox, useMailboxes } from '../../hooks/useMailboxes';

const MAIL_ROUTE = /^\/(inbox|needs-reply|flagged|drafts|sent|folder)(\/|$)/;

function MailboxMark({ m, size }: { m: Mailbox | null; size: number }) {
  return (
    <span
      aria-hidden
      className={clsx('flex shrink-0 items-center justify-center rounded-full font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]', !m && 'bg-primary')}
      style={{ width: size, height: size, background: m?.color, fontSize: Math.round(size * 0.42) }}
    >
      {m ? (m.displayName || m.email).trim().charAt(0).toUpperCase() : <Layers style={{ width: size * 0.5, height: size * 0.5 }} />}
    </span>
  );
}

const Unread = ({ n }: { n: number }) =>
  n > 0 ? <span className="rounded-full bg-primary/15 px-1.5 text-[0.625rem] font-bold tabular-nums leading-4 text-primary-ink">{n}</span> : null;

/** Which email account the mail views show (all, or one), next to the workspace switcher. Same filter as the inbox. */
export function MailboxSwitcher({ compact }: { compact?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, setAccountId } = useAccountFilter();
  const list = useMailboxes().data?.mailboxes ?? [];
  const unreadBy = useInboxUnreadByMailbox();
  if (list.length === 0) return null;

  const current = list.find((m) => m.id === accountId) ?? null;
  const totalUnread = [...unreadBy.values()].reduce((s, n) => s + n, 0);
  const pick = (id: string | null) => {
    setAccountId(id);
    if (!MAIL_ROUTE.test(location.pathname)) navigate('/inbox');
  };

  return (
    <Menu
      label="Email accounts"
      width={300}
      header={<p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">Email accounts</p>}
      trigger={(p) => (
        <button
          type="button"
          {...p}
          aria-label={`Showing ${current ? current.email : 'all email accounts'}. Switch account`}
          className={clsx(
            'group flex h-10 items-center gap-2.5 rounded-full border border-hairline bg-surface/80 pl-1.5 pr-3 text-left shadow-sm transition-[box-shadow,background-color] hover:bg-surface hover:shadow-card',
            compact && 'pr-1.5',
          )}
        >
          <MailboxMark m={current} size={28} />
          {!compact && (
            <span className="min-w-0">
              <span className="block max-w-[11rem] truncate text-[0.8125rem] font-semibold leading-tight text-ink">{current ? current.displayName || current.email : 'All accounts'}</span>
              <span className="block max-w-[11rem] truncate text-[0.625rem] font-medium uppercase tracking-wider text-subtle">
                {current ? current.email : `${list.length} account${list.length === 1 ? '' : 's'}`}
              </span>
            </span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted transition-transform group-hover:scale-110" />
        </button>
      )}
      items={[
        {
          key: 'all',
          label: 'All accounts',
          description: `${list.length} account${list.length === 1 ? '' : 's'} together`,
          icon: <MailboxMark m={null} size={28} />,
          active: !current,
          trailing: !current ? <Check className="h-4 w-4" /> : <Unread n={totalUnread} />,
          onSelect: () => pick(null),
        },
        ...list.map((m) => {
          const warn = m.status === 'needs_reauth' || m.status === 'error';
          return {
            key: m.id,
            label: m.displayName || m.email,
            description: warn ? (
              <span className="flex items-center gap-1 text-warn-ink">
                <AlertCircle className="h-3 w-3" /> Needs reconnecting
              </span>
            ) : (
              m.email
            ),
            icon: <MailboxMark m={m} size={28} />,
            active: m.id === current?.id,
            trailing: m.id === current?.id ? <Check className="h-4 w-4" /> : <Unread n={unreadBy.get(m.id) ?? 0} />,
            onSelect: () => pick(m.id),
          };
        }),
        'divider' as const,
        { key: 'add', label: 'Add an email account…', icon: <Plus className="h-4 w-4" />, onSelect: () => navigate('/settings/accounts') },
      ]}
    />
  );
}
